import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCardNumber } from "../../utils/formatCardNumber";

// Inline credit-card form. Pairs with `tokenizeCard` (../../utils/tokenizeCard.ts)
// which dispatches the captured fields to Authorize.Net Accept.js so the
// BE never sees a raw PAN.

export type InlinePaymentFields = {
  cardholderName: string;
  cardNumber: string;
  expMonth: string;
  expYear: string;
  cvc: string;
};

type Props = {
  value: InlinePaymentFields;
  onChange: (v: InlinePaymentFields) => void;
};

// Numeric-only fields with hard length caps. The card number itself is
// handled separately so it can run through `formatCardNumber` for spacing.
const NUMERIC_LIMITS: Partial<Record<keyof InlinePaymentFields, number>> = {
  expMonth: 2,
  expYear: 2,
  cvc: 4,
};

export function InlinePaymentForm({ value, onChange }: Props) {
  const set =
    (k: keyof InlinePaymentFields) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const limit = NUMERIC_LIMITS[k];
      const next =
        limit !== undefined ? raw.replace(/\D/g, "").slice(0, limit) : raw;
      onChange({ ...value, [k]: next });
    };

  const setCardNumber = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, cardNumber: formatCardNumber(e.target.value) });

  return (
    <div data-testid="inline-payment-form">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
        Payment Information
      </h3>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="cardholderName">Cardholder Name</Label>
          <Input
            autoComplete="cc-name"
            id="cardholderName"
            onChange={set("cardholderName")}
            placeholder="John Doe"
            type="text"
            value={value.cardholderName}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cardNumber">Card Number</Label>
          <Input
            autoComplete="cc-number"
            id="cardNumber"
            inputMode="numeric"
            onChange={setCardNumber}
            placeholder="4111 1111 1111 1111"
            type="text"
            value={value.cardNumber}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="expiryMonth">Month</Label>
            <Input
              aria-label="Expiration month"
              autoComplete="cc-exp-month"
              id="expiryMonth"
              inputMode="numeric"
              onChange={set("expMonth")}
              placeholder="MM"
              type="text"
              value={value.expMonth}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiryYear">Year</Label>
            <Input
              aria-label="Expiration year"
              autoComplete="cc-exp-year"
              id="expiryYear"
              inputMode="numeric"
              onChange={set("expYear")}
              placeholder="YY"
              type="text"
              value={value.expYear}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cardCode">CVV</Label>
            <Input
              aria-label="CVV"
              autoComplete="cc-csc"
              id="cardCode"
              inputMode="numeric"
              onChange={set("cvc")}
              placeholder="123"
              type="text"
              value={value.cvc}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function isInlineFormComplete(v: InlinePaymentFields): boolean {
  return Boolean(
    v.cardholderName && v.cardNumber && v.expMonth && v.expYear && v.cvc,
  );
}
